import { useState, useEffect } from "react";
import { motion } from "framer-motion";
import Cookies from "js-cookie";
import { useUser } from "../../Data/UserContext";
import { useProfileGlobalModal } from "../Profile/ProfileGlobalModalContext";
import { getAuthHeaders } from "../../utils/authHeaders";
import { normalizeSessionPayload } from "../../utils/sessionUser";
import {
  trackProductSelected,
  trackPurchaseFailed,
} from "../../utils/frontendTrackingEvents";

const SHOP_PAGE_PATH = "/shop";

const UNLOCK_COST_RUBIES = 1;
const UNLOCK_BONUS_COINS = 200;

interface FeatureUnlockPackProps {
  rubies: number;
  updateRubies: (rubies: number) => void;
  setIsAnimatingRubies?: (isAnimating: boolean) => void;
}

export default function FeatureUnlockPack({
  rubies,
  updateRubies,
  setIsAnimatingRubies,
}: FeatureUnlockPackProps) {
  const { openOwnProfile } = useProfileGlobalModal();
  const { user, setUser } = useUser();
  const [loading, setLoading] = useState(false);
  const [message, setMessage] = useState<{ text: string; error: boolean } | null>(null);
  const [allowPlayerNameEdit, setAllowPlayerNameEdit] = useState<boolean | null>(null);
  const [justUnlocked, setJustUnlocked] = useState(false);

  useEffect(() => {
    fetch(`${process.env.API_URL}/verifysession`, { method: "GET", headers: getAuthHeaders() })
      .then((res) => (res.ok ? res.json() : null))
      .then((data) => {
        if (data != null && typeof data.allow_player_name_edit === "boolean") {
          setAllowPlayerNameEdit(data.allow_player_name_edit);
        } else {
          setAllowPlayerNameEdit(false);
        }
      })
      .catch(() => setAllowPlayerNameEdit(false));
  }, [user?.allow_player_name_edit]);

  const alreadyUnlocked = allowPlayerNameEdit === true;
  const canAfford = rubies >= UNLOCK_COST_RUBIES;

  async function handleUnlock() {
    if (alreadyUnlocked || !canAfford || loading) return;
    trackProductSelected({
      user_id: user?.userId,
      product_id: "profile_unlock_pack",
      product_type: "profile_unlock_pack",
      product_name: "profile_feature_unlock_pack",
      price: UNLOCK_COST_RUBIES,
      currency: "RUBIES",
      pathname: SHOP_PAGE_PATH,
    });
    setLoading(true);
    setMessage(null);
    try {
      const res = await fetch(`${process.env.API_URL}/account/unlock-player-name-edit`, {
        method: "POST",
        headers: getAuthHeaders(),
        body: JSON.stringify({}),
      });
      const data = await res.json().catch(() => ({}));
      if (res.ok && data.allow_player_name_edit) {
        // purchase_success: server Measurement Protocol (unlock endpoint)
        setAllowPlayerNameEdit(true);
        setJustUnlocked(true);
        setTimeout(() => setJustUnlocked(false), 800);
        updateRubies(data.wallet?.rubies ?? rubies - UNLOCK_COST_RUBIES);
        setMessage({ text: `Unlocked! Player name + avatar enabled. +${UNLOCK_BONUS_COINS} coins added.`, error: false });
        const session_token = Cookies.get("c8_session_token");
        if (session_token && setUser) {
          const userRes = await fetch(
            `${process.env.API_URL}/verifysession`,
            { method: "GET", headers: getAuthHeaders() }
          );
          if (userRes.status === 200) {
            const updated = normalizeSessionPayload(await userRes.json() as Record<string, unknown>);
            setUser(updated);
          }
        }
        // After in-drawer animation: close drawer, reopen My profile, then run profile name/avatar animation
        setTimeout(() => {
          window.dispatchEvent(new CustomEvent("closeShopDrawer"));
        }, 1000);
        setTimeout(() => {
          openOwnProfile({ openEdit: true });
        }, 1100);
        setTimeout(() => {
          sessionStorage.setItem("c8_just_unlocked_player_name", "1");
          window.dispatchEvent(new CustomEvent("runProfileUnlockAnimation"));
        }, 1500);
      } else if (data.alreadyUnlocked) {
        setAllowPlayerNameEdit(true);
        setJustUnlocked(true);
        setTimeout(() => setJustUnlocked(false), 800);
        setMessage({ text: "Already unlocked.", error: false });
        if (setUser && user) setUser({ ...user, allow_player_name_edit: true });
        setTimeout(() => window.dispatchEvent(new CustomEvent("closeShopDrawer")), 1000);
        setTimeout(() => openOwnProfile({ openEdit: true }), 1100);
        setTimeout(() => {
          sessionStorage.setItem("c8_just_unlocked_player_name", "1");
          window.dispatchEvent(new CustomEvent("runProfileUnlockAnimation"));
        }, 1500);
      } else {
        trackPurchaseFailed({
          user_id: user?.userId,
          product_id: "profile_unlock_pack",
          product_type: "profile_unlock_pack",
          amount: UNLOCK_COST_RUBIES,
          currency: "RUBIES",
          error_type: res.status >= 500 ? "network_error" : "validation_error",
          pathname: SHOP_PAGE_PATH,
        });
        setMessage({
          text: data?.message ?? "Could not unlock. You need 1 ruby.",
          error: true,
        });
      }
    } catch {
      trackPurchaseFailed({
        user_id: user?.userId,
        product_id: "profile_unlock_pack",
        product_type: "profile_unlock_pack",
        amount: UNLOCK_COST_RUBIES,
        currency: "RUBIES",
        error_type: "network_error",
        pathname: SHOP_PAGE_PATH,
      });
      setMessage({ text: "Something went wrong. Please try again.", error: true });
    } finally {
      setLoading(false);
    }
  }

  return (
    <motion.div
      className="flex flex-col justify-center items-center gap-1 relative px-2 py-3 bg-white w-full min-w-[140px]"
      initial={false}
      animate={
        justUnlocked
          ? { scale: [1, 1.08, 1], transition: { duration: 0.4, times: [0, 0.5, 1] } }
          : { scale: 1 }
      }
    >
      <img
        className={`w-[40px] h-[40px] object-contain ${!alreadyUnlocked ? "opacity-60" : ""}`}
        src="/assets/currency-ruby.png"
        alt=""
        aria-hidden
      />
      <p
        className={`font-anton text-[#1B062F] font-[400] text-[14px] text-center leading-tight ${
          !alreadyUnlocked ? "opacity-60" : ""
        }`}
      >
        Profile Unlock Pack
      </p>
      <p
        className={`font-chakra text-[#1B062F] text-[11px] text-center normal-case ${
          !alreadyUnlocked ? "opacity-60" : ""
        }`}
      >
        Unlock player name + avatar change
      </p>
      <div
        className={`flex items-center justify-center gap-1 ${
          !alreadyUnlocked ? "opacity-60" : ""
        }`}
      >
        <span className="font-chakra text-[#1B062F] text-[11px] normal-case">+{UNLOCK_BONUS_COINS}</span>
        <img
          src="/img/coin_sm.png"
          alt="Coins bonus"
          className="w-[17px] h-[10px] object-contain"
        />
      </div>
      {message && (
        <p
          className={`font-chakra text-[11px] text-center normal-case ${
            message.error ? "text-[#dc2626]" : "text-[#16a34a]"
          }`}
        >
          {message.text}
        </p>
      )}
      {alreadyUnlocked ? (
        <motion.button
          type="button"
          onClick={() => {
            window.dispatchEvent(new CustomEvent("closeShopDrawer"));
            openOwnProfile({ openEdit: true });
          }}
          className="font-anton uppercase text-[14px] flex items-center justify-center gap-1 bg-gray-400 w-[100px] h-[40px] text-[#1B062F] cursor-pointer"
          initial={justUnlocked ? { scale: 0.5, opacity: 0 } : false}
          animate={{ scale: 1, opacity: 1 }}
          transition={{ type: "spring", stiffness: 400, damping: 20 }}
        >
          UNLOCKED
        </motion.button>
      ) : (
        <button
          type="button"
          onClick={handleUnlock}
          disabled={loading || !canAfford}
          className={`font-anton uppercase font-[400] text-[14px] flex flex-row items-center justify-center gap-1 whitespace-nowrap w-[100px] h-[40px] text-center disabled:cursor-not-allowed ${
            canAfford ? "bg-lime text-[#1B062F]" : "bg-gray-300 text-[#1B062F]"
          }`}
        >
          {loading
            ? "…"
            : canAfford
              ? <>Unlock for {UNLOCK_COST_RUBIES} <img src="/assets/currency-ruby.png" alt="" className="w-4 h-4 object-contain inline-block" aria-hidden /></>
              : `Need ${UNLOCK_COST_RUBIES} ruby`}
        </button>
      )}
    </motion.div>
  );
}
